import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { structuralGroupService } from "@modules/organizationalStructure/structuralGroups/services/structuralGroups";
import { nomenclaturesOz } from "@modules/organizationalStructure/nomenclatures/services/nomenclaturesOz";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import PreviousPage from "@shared/components/PreviousPage";
import CustomDatePicker from "@shared/components/CustomDatePicker";

export default function StructuralGroupRights() {
    const { t } = useTranslation();
    const location = useLocation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rights, setRights] = useState([]);
    const [groupedRights, setGroupedRights] = useState(null);
    const [nomenclatures, setNomenclatures] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(null);
    const [permanentDate, setPermanentDate] = useState(false);
    const [priority, setPriority] = useState('');
    const [hierarchyOptions, setHierarchyOptions] = useState([]);
    const [selectedHierarchy, setSelectedHierarchy] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [loadingSelectionId, setLoadingSelectionId] = useState(null);
    const { register, handleSubmit } = useForm();

    // Helper function to format dates in Bulgarian format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        }
        return dateString;
    };

    const getApiErrorMessage = (error) => error.response?.data?.message || t('common.errorMessage');

    // Get position data and company ID from navigation state
    const positionData = location.state?.structuralGroup;
    const orgUnitData = location.state?.orgUnit;
    const companyId = location.state?.companyId;
    const positionId = positionData?.id;
    const orgUnitId = orgUnitData?.id;

    const getCloseDateMinDate = () => {
        if (!fromDate) return undefined;
        const date = fromDate instanceof Date ? new Date(fromDate) : new Date(fromDate);
        if (isNaN(date.getTime())) return undefined;
        date.setHours(12, 0, 0, 0);
        return date;
    };

    const handleFromDateChange = (date) => {
        setFromDate(date);
        if (toDate && date) {
            const from = date instanceof Date ? date : new Date(date);
            const to = toDate instanceof Date ? toDate : new Date(toDate);
            if (!isNaN(from.getTime()) && !isNaN(to.getTime()) && to < from) {
                setToDate(null);
            }
        }
    };

    const applyItemRightsToCheckboxes = (itemRights) => {
        const rightsCheckboxes = document.querySelectorAll('input[type="checkbox"][name^="right_"], input[type="checkbox"][name^="nomenclature_"]');
        rightsCheckboxes.forEach(checkbox => {
            checkbox.checked = false;
        });

        if (itemRights?.rightKeyIds) {
            itemRights.rightKeyIds.forEach(rightId => {
                const checkbox = document.querySelector(`input[name="right_${rightId}"]`);
                if (checkbox) {
                    checkbox.checked = true;
                }
            });
        }

        if (itemRights?.rightPosSalaryIds) {
            itemRights.rightPosSalaryIds.forEach(nomenclatureId => {
                const checkbox = document.querySelector(`input[name="nomenclature_${nomenclatureId}"]`);
                if (checkbox) {
                    checkbox.checked = true;
                }
            });
        }
    };

    const clearRightsCheckboxes = () => {
        const rightsCheckboxes = document.querySelectorAll('input[type="checkbox"][name^="right_"], input[type="checkbox"][name^="nomenclature_"]');
        rightsCheckboxes.forEach(checkbox => {
            checkbox.checked = false;
        });
    };

    const loadItemRights = async (item, { populateForm = false } = {}) => {
        try {
            setLoadingSelectionId(item.id);
            const itemRights = await structuralGroupService.getPositionRightsById(item.id);

            applyItemRightsToCheckboxes(itemRights);

            if (populateForm) {
                setFromDate(itemRights.openDate ? new Date(itemRights.openDate) : new Date());
                setToDate(itemRights.closeDate ? new Date(itemRights.closeDate) : null);
                setPermanentDate(!itemRights.closeDate);
                setPriority(itemRights.orderPriority?.toString() || '');
                setSelectedHierarchy(itemRights.orgUnitId?.toString() || '');
            }

            return itemRights;
        } catch (error) {
            console.error('Error loading item rights:', error);
            notifyError(getApiErrorMessage(error));
            return null;
        } finally {
            setLoadingSelectionId(null);
        }
    };

    const handleRowSelect = async (item) => {
        setSelectedItem(item);
        setShowAddForm(false);
        setEditingItem(null);
        await loadItemRights(item);
    };

    const handleAdd = () => {
        setShowAddForm(true);
        setEditingItem(null);
        setSelectedItem(null);
        clearRightsCheckboxes();
    };

    const handleEdit = async (item) => {
        setSelectedItem(item);
        setEditingItem(item);
        setShowAddForm(true);
        await loadItemRights(item, { populateForm: true });
    };

    const handleDelete = async (item) => {
        try {
            setLoading(true);
            await structuralGroupService.deletePositionRights(item.id);
            notifySuccess(t('common.deletedSuccessfully'));

            if (selectedItem?.id === item.id) {
                setSelectedItem(null);
                clearRightsCheckboxes();
            }
            
            // Refresh the table data
            const tableData = await structuralGroupService.getPositionRights(positionId);
            setData(tableData);
        } catch (error) {
            console.error('Error deleting item:', error);
            notifyError(getApiErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelAdd = async () => {
        setShowAddForm(false);
        setEditingItem(null);
        setFromDate(new Date().toISOString().split('T')[0]);
        setToDate('');
        setPermanentDate(false);
        setPriority('');
        setSelectedHierarchy('');

        if (selectedItem) {
            await loadItemRights(selectedItem);
        } else {
            clearRightsCheckboxes();
        }
    };

    const handleSaveAdd = async () => {
        try {
            setLoading(true);
            
            // Collect all checked right IDs from the form
            const formData = new FormData();
            const rightKeyIds = [];
            const rightPosSalaryIds = [];
            
            // Get all checkbox inputs
            const checkboxes = document.querySelectorAll('input[type="checkbox"][name^="right_"], input[type="checkbox"][name^="nomenclature_"]');
            checkboxes.forEach(checkbox => {
                if (checkbox.checked && checkbox.name.startsWith('right_')) {
                    const rightId = checkbox.name.replace('right_', '');
                    rightKeyIds.push(parseInt(rightId));
                }
                if (checkbox.checked && checkbox.name.startsWith('nomenclature_')) {
                    const nomenclatureId = checkbox.name.replace('nomenclature_', '');
                    rightPosSalaryIds.push(parseInt(nomenclatureId));
                }
            });
            
            // Prepare the data for the API call
            const requestData = {
                positionId: positionId,
                orgUnitId: parseInt(selectedHierarchy),
                openDate: fromDate ? fromDate.toISOString().split('T')[0] : '',
                closeDate: permanentDate ? "2099-12-12" : (toDate ? toDate.toISOString().split('T')[0] : ''),
                orderPriority: parseInt(priority) || 1,
                rightKeyIds: rightKeyIds,
                rightPosSalaryIds: rightPosSalaryIds
            };
            
            
            // Call the API - either create or update
            if (editingItem) {
                await structuralGroupService.updatePositionRights(editingItem.id, requestData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await structuralGroupService.createPositionRights(requestData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            // Reset form and refresh data
            setShowAddForm(false);
            setEditingItem(null);
            setSelectedItem(null);
            setFromDate(new Date().toISOString().split('T')[0]);
            setToDate('');
            setPermanentDate(false);
            setPriority('');
            setSelectedHierarchy('');
            
            // Refresh the table data
            const tableData = await structuralGroupService.getPositionRights(positionId);
            setData(tableData);
            
            clearRightsCheckboxes();
            
        } catch (error) {
            console.error('Error saving position rights:', error);
            notifyError(getApiErrorMessage(error));
            setError('Failed to save position rights');
        } finally {
            setLoading(false);
        }
    };

    // Step 1: Grouping function that matches exact layout structure
    const groupRightsByLayout = (rightsData) => {
        // Sort by ordNo to maintain the intended order from server
        const sortedRights = rightsData.sort((a, b) => a.ordNo - b.ordNo);

        // Find HR approval rights to move to section 2
        const hrApprovalRights = sortedRights.filter(right =>
            right.approvalKey === 'HR_PAID_LEAVE' ||
            right.approvalKey === 'HR_BUSINESS_TRIP' ||
            right.approvalKey === 'HR_EXTRA_HOURS' ||
            right.approvalKey === 'HR_SCHEDULE' ||
            right.approvalKey === 'AC_BUSINESS_TRIP'
        );

        // Get other rights (excluding HR, section 1 rights, section 3 rights, merged rights)
        const otherRights = sortedRights.filter(right =>
            right.approvalKey !== 'HR_PAID_LEAVE' &&
            right.approvalKey !== 'HR_BUSINESS_TRIP' &&
            right.approvalKey !== 'HR_EXTRA_HOURS' &&
            right.approvalKey !== 'HR_SCHEDULE' &&
            right.approvalKey !== 'AC_BUSINESS_TRIP' &&
            right.approvalKey !== 'ANNUAL_EVALUATION' &&
            right.approvalKey !== 'MANAGER' &&
            right.approvalKey !== 'MANAGER_REPORTS' &&
            right.approvalKey !== 'ACCESS_SALARY' &&
            right.approvalKey !== 'RFH_MANAGER' &&
            right.approvalKey !== 'RFH_HR' &&
            right.approvalKey !== 'RFH_FIRST_APP' &&
            right.approvalKey !== 'RFH_SECOND_APP' &&
            right.approvalKey !== 'RFH_ADMINISTRATION' &&
            right.approvalKey !== 'FIRST_APP_HOME_OFFICE' &&
            right.approvalKey !== 'SECOND_APP_HOME_OFFICE' &&
            right.approvalKey !== 'HR_APP_HOME_OFFICE' &&
            right.approvalKey !== 'MANAGER_APP_EXTRA_HOUR_ORDER' &&
            right.approvalKey !== 'HR_APP_EXTRA_HOUR_ORDER' &&
            right.approvalKey !== 'EMPLOYEE_EXTRA_HOUR_ORDER'
        );

        return {
            // Section 1: Custom ordered rights - 3 columns with specific order
            section1: {
                column1: [
                    sortedRights.find(right => right.approvalKey === 'PAID_LEAVE1'),      // Първи подпис на заявка за отпуск
                    sortedRights.find(right => right.approvalKey === 'BT_REQUEST'),       // Подпис на заповед за командировка
                    sortedRights.find(right => right.approvalKey === 'MANAGER'),          // Ръководител
                    sortedRights.find(right => right.approvalKey === 'EXTRA_HOURS')       // Въвеждане на Извънреден труд
                ].filter(Boolean), // Remove any undefined values

                column2: [
                    sortedRights.find(right => right.approvalKey === 'PAID_LEAVE2'),      // Втори подпис на заявка за отпуск
                    sortedRights.find(right => right.approvalKey === 'BT_REPORT'),        // Подпис на доклад от командировка
                    sortedRights.find(right => right.approvalKey === 'MANAGER_REPORTS'),  // Меню "Справки за мениджър"
                    sortedRights.find(right => right.approvalKey === 'SCHEDULE')          // Въвеждане на График
                ].filter(Boolean), // Remove any undefined values

                column3: [
                    sortedRights.find(right => right.approvalKey === 'ANNUAL_EVALUATION'), // Оформяне на годишна оценка
                    sortedRights.find(right => right.approvalKey === 'ACCESS_SALARY')      // Меню "Информация за заплати"
                ].filter(Boolean) // Remove any undefined values
            },

            // Section 2: HR approval rights + accounting right - 2-2-1 distribution
            section2: {
                column1: hrApprovalRights.slice(0, 2),  // First 2 HR rights
                column2: hrApprovalRights.slice(2, 4),  // Next 2 HR rights
                column3: hrApprovalRights.slice(4, 5)   // Accounting business trip right
            },

            // Section 3: Custom ordered RFH rights - 2-2-1 distribution  
            section3: {
                column1: [
                    sortedRights.find(right => right.approvalKey === 'RFH_MANAGER'),      // Форма за назначаване/промяна (Мениджър)
                    sortedRights.find(right => right.approvalKey === 'RFH_HR')           // Форма за назначаване/промяна (Човешки ресурси)
                ].filter(Boolean), // Remove any undefined values

                column2: [
                    sortedRights.find(right => right.approvalKey === 'RFH_FIRST_APP'),   // Форма за назначаване/промяна (Първo одобрение)
                    sortedRights.find(right => right.approvalKey === 'RFH_SECOND_APP')   // Форма за назначаване/промяна (Второ одобрение)
                ].filter(Boolean), // Remove any undefined values

                column3: [
                    sortedRights.find(right => right.approvalKey === 'RFH_ADMINISTRATION') // Форма за назначаване/промяна (Администрация)
                ].filter(Boolean) // Remove any undefined values
            },

            // Section 4: Custom ordered merged rights - 2-2-2 distribution
            section4: {
                column1: [
                    sortedRights.find(right => right.approvalKey === 'FIRST_APP_HOME_OFFICE'),     // Първи подпис за работа от вкъщи
                    sortedRights.find(right => right.approvalKey === 'EMPLOYEE_EXTRA_HOUR_ORDER')  // Заявка от служителя за заповед за извънреден труд
                ].filter(Boolean), // Remove any undefined values

                column2: [
                    sortedRights.find(right => right.approvalKey === 'SECOND_APP_HOME_OFFICE'),    // Втори подпис за работа от вкъщи
                    sortedRights.find(right => right.approvalKey === 'MANAGER_APP_EXTRA_HOUR_ORDER') // Подпис на мениджъра за заповед за извънреден труд
                ].filter(Boolean), // Remove any undefined valuesgetPositionRights

                column3: [
                    sortedRights.find(right => right.approvalKey === 'HR_APP_HOME_OFFICE'),        // HR подпис за работа от вкъщи
                    sortedRights.find(right => right.approvalKey === 'HR_APP_EXTRA_HOUR_ORDER')    // Подпис на HR за заповед за извънреден труд
                ].filter(Boolean) // Remove any undefined values
            },

            // Section 5: Remaining other rights - single row
            section5: otherRights.slice(9, 17)    // Remaining other rights
        };
    };


    // Fetch table data using getPositionRights
    useEffect(() => {
        const fetchTableData = async () => {
            if (!positionId) {
                setData([]);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const tableData = await structuralGroupService.getPositionRights(positionId);
                setData(tableData);
            } catch (error) {
                console.error('Error fetching position rights:', error);
                setError('Failed to load position rights data');
            } finally {
                setLoading(false);
            }
        };
        fetchTableData();
    }, [positionId]);

    useEffect(() => {
        const fetchNomenclatures = async () => {
            const nomenclatures = await nomenclaturesOz.getNomenclatureItems(3);
            setNomenclatures(nomenclatures);
        };
        fetchNomenclatures();
    }, []);

    // Fetch hierarchy options
    useEffect(() => {
        const fetchHierarchyOptions = async () => {
            if (!orgUnitId || !companyId) {
                return;
            }
            try {
                setLoading(true);
                const hierarchyData = await structuralGroupService.getOrgUnitHierarchy(orgUnitId, companyId);
                setHierarchyOptions(hierarchyData);
            } catch (error) {
                console.error('Error fetching hierarchy options:', error);
                setError('Failed to load hierarchy options');
            } finally {
                setLoading(false);
            }
        };
        fetchHierarchyOptions();
    }, [orgUnitId, companyId]);
    // Step 2: Fetch rights data from server
    useEffect(() => {
        const fetchRights = async () => {
            try {
                setLoading(true);
                const rightsData = await structuralGroupService.getCheckboxPositionRights();
                setRights(rightsData);
                setGroupedRights(groupRightsByLayout(rightsData));
            } catch (error) {
                console.error('Error fetching rights:', error);
                setError('Failed to load rights data');
            } finally {
                setLoading(false);
            }
        };

        fetchRights();
    }, []);

    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={handleAdd}
                title="Add Rights"
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>
        </>
    );

    return (
        <div className="flex-column align-items-center width-100">

            {/* First Table - Position Information */}
            <h2 className="title">{t('organizationalStructure.structuralGroups.rights.title')}</h2>
            <div className="table-responsive custom-table-container width-50">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>{t('organizationalStructure.structuralGroups.rights.code')}</th>
                            <th>{t('organizationalStructure.structuralGroups.rights.position')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {positionData ? (
                            <tr>
                                <td>{positionData.positionNumber || positionData.jobCode || ""}</td>
                                <td>{positionData.hrOrgUnit?.longDescription || positionData.longDescription || positionData.name || ""}</td>
                            </tr>
                        ) : (
                            <tr>
                                <td colSpan="2" className="text-center">No position data available</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="flex-row justify-content-center mt-3">
                {/* Step 3: Section 1 - Column 1 (First 4 rights) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section1.column1.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 1 - Column 2 (Next 4 rights) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section1.column2.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 1 - Column 3 (Next 4 rights) */}
                <div className="flex-column">
                    {groupedRights?.section1.column3.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

            </div>
            <div className="flex-row mt-3 justify-content-center">
                {/* Step 3: Section 2 - Column 1 (2 rights) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section2.column1.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 2 - Column 2 (1 right) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section2.column2.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 2 - Column 3 (1 right) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section2.column3.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

            </div>
            <div className="flex-row mt-3 justify-content-center">
                {/* Step 3: Section 3 - Column 1 (2 rights) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section3.column1.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 3 - Column 2 (1 right) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section3.column2.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 3 - Column 3 (1 right) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section3.column3.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex-row mt-3 justify-content-center">
                {/* Step 3: Section 4 - Column 1 (2 rights) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section4.column1.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 4 - Column 2 (2 rights) */}
                <div className="flex-column padding-right-20">
                    {groupedRights?.section4.column2.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Step 3: Section 4 - Column 3 (2 rights) */}
                <div className="flex-column">
                    {groupedRights?.section4.column3.map((right) => (
                        <div key={right.id} className="input-checkbox-wrapper">
                            <input
                                type="checkbox"
                                {...register(`right_${right.id}`)}
                                disabled={!showAddForm}
                            />
                            <span className="span-bold">
                                {right.rightDescription}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            {/* NomenclaturesOz Checkboxes */}
            <div className="flex-row mt-3 justify-content-center">
                {nomenclatures.map((nomenclature, index) => (
                    <div key={nomenclature.id} className={`input-checkbox-wrapper ${index < nomenclatures.length - 1 ? 'padding-right-20' : ''}`}>
                        <input
                            type="checkbox"
                            {...register(`nomenclature_${nomenclature.nomenclatureItem.id}`)}
                            disabled={!showAddForm}
                        />
                        <span className="span-bold">
                            {nomenclature.name}
                        </span>
                    </div>
                ))}
            </div>

            {/* Second Table with Data Fetching and Add Functionality */}
            <div className="flex-row justify-content-center" style={{ marginTop: '20px' }}>
                <PreviousPage 
                 customPath="/structure-and-positions" 
                 preserveState={true} />
                {actionButtons}
            </div>

            <div className="table-responsive custom-table-container width-100" style={{ marginTop: '20px' }}>
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>{t('organizationalStructure.structuralGroups.rights.code')}</th>
                            <th>{t('organizationalStructure.structuralGroups.rights.structuralGroup')}</th>
                            <th>{t('organizationalStructure.structuralGroups.rights.fromDate')}</th>
                            <th>{t('organizationalStructure.structuralGroups.rights.toDate')}</th>
                            <th>{t('organizationalStructure.structuralGroups.rights.priority')}</th>
                            <th>{t('organizationalStructure.structuralGroups.rights.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showAddForm && (
                            <tr className="add-row">
                                <td>
                                    {/* Code column - empty for now */}
                                </td>
                                <td>
                                <select
                                        className="form-control"
                                        value={selectedHierarchy}
                                        onChange={(e) => setSelectedHierarchy(e.target.value)}
                                    >
                                        <option value="">Select Structural Group</option>
                                        {hierarchyOptions.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.levelCode}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td>
                                    <CustomDatePicker
                                        name="fromDate"
                                        value={fromDate}
                                        onChange={handleFromDateChange}
                                        isSmall={true}
                                        placeholderText={t('common.fromDate')}
                                        renderInPortal={true}
                                        popperPlacement="top-start"
                                    />
                                </td>
                                <td>
                                    <CustomDatePicker
                                        name="toDate"
                                        value={toDate}
                                        onChange={setToDate}
                                        disabled={permanentDate}
                                        isSmall={true}
                                        placeholderText={t('common.toDate')}
                                        minDate={getCloseDateMinDate()}
                                        renderInPortal={true}
                                        popperPlacement="top-start"
                                    />
                                </td>
                                <td>
                                  <div className="input-checkbox-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={permanentDate}
                                        onChange={(e) => setPermanentDate(e.target.checked)}
                                    />
                                    <span className="span">
                                        {t('organizationalStructure.structuralGroups.rights.permanent')}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                    <input
                                        className="form-control"
                                        type="number"
                                        value={priority}
                                        onChange={(e) => setPriority(e.target.value)}
                                        placeholder="Priority"
                                    />
                                </td>
                                <td>
                                    <div className="flex-row" style={{ gap: '5px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={handleSaveAdd}
                                        >
                                            Save
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={handleCancelAdd}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {loading ? (
                            <tr>
                                <td colSpan="6" className="text-center">Loading rights data...</td>
                            </tr>
                        ) : !data || (Array.isArray(data) && data.length === 0) ? (
                            <tr>
                                <td colSpan="6" className="text-center">No rights data available</td>
                            </tr>
                        ) : (
                            // Handle both single object and array responses
                            (Array.isArray(data) ? data : [data]).map((item) => (
                                <tr
                                    key={item.id}
                                    onClick={() => handleRowSelect(item)}
                                    className={selectedItem?.id === item.id ? 'selected-row' : ''}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: selectedItem?.id === item.id ? '#e3f2fd' : undefined,
                                    }}
                                >
                                    <td>{item.code || ""}</td>
                                    <td>{item.orgUnit || ""}</td>
                                    <td>{formatDate(item.openDate)}</td>
                                    <td>{item.closeDate ? (item.closeDate.split('T')[0] === '2099-12-11' ? 'Permanent' : formatDate(item.closeDate)) : 'Permanent'}</td>
                                    <td>{item.orderPriority}</td>
                                    <td>
                                        {loadingSelectionId === item.id && (
                                            <span className="text-muted" style={{ marginRight: '8px' }}>...</span>
                                        )}
                                        <button
                                            className="icon-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEdit(item);
                                            }}
                                            title={t('common.edit')}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            className="icon-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(item);
                                            }}
                                            title={t('common.remove')}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

        </div>

    );
}

